import React, { useEffect, useState } from "react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useSEO } from "../lib/seo";
import { Check, CrownSimple, Lightning } from "@phosphor-icons/react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

export default function Pricing() {
  const [packages, setPackages] = useState([]);
  useSEO({
    title: "Pricing for Pros",
    description: "Simple pricing for skilled workers and freelancers. Verified Pro badges, lead unlocks, and marketplace subscriptions.",
    path: "/pricing",
  });
  const { user } = useAuth();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(null);

  useEffect(() => { api.get("/payments/packages").then((r) => setPackages(r.data)); }, []);

  const checkout = async (pkgId) => {
    if (!user) return navigate("/login");
    setBusy(pkgId);
    try {
      const { data } = await api.post("/payments/checkout", {
        package_id: pkgId,
        origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (e) {
      toast.error("Could not start checkout");
      setBusy(null);
    }
  };

  const features = {
    starter_monthly: ["5 leads/month", "Profile listing", "In-app chat", "Email support"],
    pro_monthly: ["Unlimited leads", "Priority placement", "Pro badge", "Priority support", "Analytics"],
    lead_unlock: ["Unlock 1 specific lead", "Pay-as-you-go", "No commitment"],
  };

  return (
    <div className="min-h-screen bg-white">
      <Header />
      <div className="pt-24 pb-16 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-xs uppercase tracking-[0.2em] font-semibold text-slate-500 mb-2">Pricing</div>
        <h1 className="font-display text-4xl sm:text-5xl mb-3">Grow your business.</h1>
        <p className="text-base text-slate-600 mb-12 max-w-xl">All plans billed in CAD. Cancel anytime. No setup fees.</p>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-slate-200">
          {packages.map((p) => (
            <div key={p.id} className={`bg-white p-8 ${p.id === "pro_monthly" ? "lg:scale-[1.02] lg:-mt-2 lg:-mb-2 ring-4 ring-brand z-10 relative" : ""}`}>
              {p.id === "pro_monthly" && (
                <div className="absolute -top-3 left-8 bg-accent-red text-white text-[10px] uppercase tracking-[0.2em] px-2 py-1 font-semibold">Most popular</div>
              )}
              <div className="text-xs uppercase tracking-[0.2em] font-semibold text-slate-500 mb-2">{p.type === "subscription" ? "Subscription" : "One-time"}</div>
              <h2 className="font-display text-2xl mb-2">{p.name}</h2>
              <div className="font-display text-5xl mb-6">${p.amount}<span className="text-base text-slate-500 font-sans font-normal">{p.type === "subscription" ? "/mo" : ""}</span></div>
              <ul className="space-y-2 mb-8">
                {(features[p.id] || []).map((f) => (
                  <li key={f} className="flex items-start gap-2 text-sm"><Check size={16} weight="bold" className="text-brand mt-0.5" /> {f}</li>
                ))}
              </ul>
              <button
                onClick={() => checkout(p.id)}
                disabled={busy === p.id}
                className={`w-full py-3 font-semibold transition-colors disabled:opacity-50 ${p.id === "pro_monthly" ? "bg-brand text-white hover:bg-[#122148]" : "bg-slate-900 text-white hover:bg-slate-800"}`}
                data-testid={`buy-${p.id}`}
              >
                {busy === p.id ? "Loading…" : p.type === "subscription" ? "Subscribe" : "Buy lead"}
              </button>
            </div>
          ))}
        </div>

        <div className="mt-12 border border-slate-200 p-6 text-sm text-slate-600">
          <strong className="text-slate-900">Refund Policy:</strong> Subscriptions are non-refundable after billing. Single-lead unlocks are refundable if the customer is unresponsive within 48 hours. See <a className="text-brand underline" href="/legal/refund">full policy</a>.
        </div>
      </div>
      <Footer />
    </div>
  );
}
